import { pgTable, text, boolean, timestamp, integer, index, uniqueIndex, foreignKey, jsonb, numeric } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import {
  gameType,
  lobbyStatus,
  lobbyPlayerStatus,
  gameSessionStatus,
  playerReportCategory,
  playerReportStatus,
  achievementCategory,
  achievementConditionType
} from './enums';
import { user } from './user';
import { adminUser } from './admin';
import { chatRoom } from './messaging';

// ==========================================
// GAME - Определение игры
// ==========================================
export const game = pgTable('Game', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  // Тип игры
  type: gameType().notNull(),

  // Информация об игре
  name: text().notNull(),
  description: text(),

  // Настройки
  minPlayers: integer().default(1).notNull(),
  maxPlayers: integer().default(10).notNull(),
  settings: jsonb(), // Дефолтные настройки игры

  // Флаги
  isActive: boolean().default(true).notNull(),

  // Timestamps
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  uniqueIndex('Game_type_key').using('btree', table.type.asc().nullsLast()),
  index('Game_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
]);

// ==========================================
// GAME LOBBY - Лобби для мультиплеера
// ==========================================
export const gameLobby = pgTable('GameLobby', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  gameId: integer().notNull(),
  hostUserId: integer().notNull(),

  // Информация о лобби
  name: text(),
  password: text(), // Хеш пароля (null = открытое)
  isPrivate: boolean().default(false).notNull(),

  // Язык для matchmaking
  language: text().default('ru').notNull(),

  // Статус
  status: lobbyStatus().default('WAITING').notNull(),

  // Настройки
  maxPlayers: integer().default(10).notNull(),
  settings: jsonb(), // Переопределение настроек игры

  // Связь с чатом
  chatRoomId: integer(),

  // Время жизни лобби
  expiresAt: timestamp({ precision: 3, mode: 'date' }),

  // Timestamps
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('GameLobby_gameId_idx').using('btree', table.gameId.asc().nullsLast()),
  index('GameLobby_hostUserId_idx').using('btree', table.hostUserId.asc().nullsLast()),
  index('GameLobby_status_idx').using('btree', table.status.asc().nullsLast()),
  index('GameLobby_language_idx').using('btree', table.language.asc().nullsLast()),
  index('GameLobby_isPrivate_idx').using('btree', table.isPrivate.asc().nullsLast()),
  // Для matchmaking: открытые лобби по языку и статусу
  index('GameLobby_matchmaking_idx').using('btree',
    table.status.asc().nullsLast(),
    table.isPrivate.asc().nullsLast(),
    table.language.asc().nullsLast()
  ),
  foreignKey({
    columns: [table.gameId],
    foreignColumns: [game.id],
    name: 'GameLobby_gameId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.hostUserId],
    foreignColumns: [user.id],
    name: 'GameLobby_hostUserId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.chatRoomId],
    foreignColumns: [chatRoom.id],
    name: 'GameLobby_chatRoomId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

// ==========================================
// LOBBY PLAYER - Игрок в лобби
// ==========================================
export const lobbyPlayer = pgTable('LobbyPlayer', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  lobbyId: integer().notNull(),
  userId: integer().notNull(),

  // Статус игрока
  status: lobbyPlayerStatus().default('WAITING').notNull(),
  isReady: boolean().default(false).notNull(),

  // Порядок хода (устанавливается при старте игры)
  turnOrder: integer(),

  // Счёт в текущей игре
  score: integer().default(0).notNull(),

  // Timestamps
  joinedAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  leftAt: timestamp({ precision: 3, mode: 'date' }),

  // Кик админом (для модерации)
  kickedByAdminId: integer(),
  kickReason: text(),
}, (table) => [
  // TEMPORARY: Убран unique constraint для тестирования с двух устройств
  // TODO: Вернуть constraint после тестирования
  // uniqueIndex('LobbyPlayer_lobby_user_key').using('btree',
  //   table.lobbyId.asc().nullsLast(),
  //   table.userId.asc().nullsLast()
  // ),
  index('LobbyPlayer_lobbyId_idx').using('btree', table.lobbyId.asc().nullsLast()),
  index('LobbyPlayer_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('LobbyPlayer_status_idx').using('btree', table.status.asc().nullsLast()),
  foreignKey({
    columns: [table.lobbyId],
    foreignColumns: [gameLobby.id],
    name: 'LobbyPlayer_lobbyId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'LobbyPlayer_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.kickedByAdminId],
    foreignColumns: [adminUser.id],
    name: 'LobbyPlayer_kickedByAdminId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

// ==========================================
// GAME SESSION - Игровая сессия
// ==========================================
export const gameSession = pgTable('GameSession', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  // Связи
  lobbyId: integer(), // null для соло игр
  gameId: integer().notNull(),

  // Статус
  status: gameSessionStatus().default('ACTIVE').notNull(),

  // Состояние игры (JSONB)
  gameState: jsonb().notNull(),

  // Текущий ход
  currentTurnUserId: integer(),
  turnNumber: integer().default(0).notNull(),

  // Результат
  winnerId: integer(),

  // Timestamps
  startedAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  finishedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('GameSession_lobbyId_idx').using('btree', table.lobbyId.asc().nullsLast()),
  index('GameSession_gameId_idx').using('btree', table.gameId.asc().nullsLast()),
  index('GameSession_status_idx').using('btree', table.status.asc().nullsLast()),
  index('GameSession_winnerId_idx').using('btree', table.winnerId.asc().nullsLast()),
  foreignKey({
    columns: [table.lobbyId],
    foreignColumns: [gameLobby.id],
    name: 'GameSession_lobbyId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.gameId],
    foreignColumns: [game.id],
    name: 'GameSession_gameId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.currentTurnUserId],
    foreignColumns: [user.id],
    name: 'GameSession_currentTurnUserId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.winnerId],
    foreignColumns: [user.id],
    name: 'GameSession_winnerId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

// ==========================================
// GAME MOVE - Ходы в игре
// ==========================================
export const gameMove = pgTable('GameMove', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  sessionId: integer().notNull(),
  userId: integer().notNull(),

  // Тип и данные хода
  moveType: text().notNull(), // 'GUESS_COLOR', 'SET_NUMBER', 'MAKE_GUESS', 'MARK_CORRECT'
  moveData: jsonb().notNull(), // { color: 'WHITE' }, { number: 42 }, { guess: 'больше 100' }

  // Результат хода
  result: jsonb(), // { isCorrect: true, score: 1 }

  // Timestamps
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('GameMove_sessionId_idx').using('btree', table.sessionId.asc().nullsLast()),
  index('GameMove_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('GameMove_sessionId_createdAt_idx').using('btree',
    table.sessionId.asc().nullsLast(),
    table.createdAt.asc().nullsLast()
  ),
  foreignKey({
    columns: [table.sessionId],
    foreignColumns: [gameSession.id],
    name: 'GameMove_sessionId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'GameMove_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// USER GAME STATS - Общая статистика пользователя
// ==========================================
export const userGameStats = pgTable('UserGameStats', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  userId: integer().notNull(),
  gameId: integer().notNull(),

  // Статистика
  totalGames: integer().default(0).notNull(),
  wins: integer().default(0).notNull(),
  losses: integer().default(0).notNull(),
  draws: integer().default(0).notNull(),

  // Очки
  totalScore: integer().default(0).notNull(),
  bestScore: integer().default(0).notNull(),
  avgScore: numeric({ precision: 10, scale: 2 }).default('0').notNull(),

  // Серии (для достижений)
  currentStreak: integer().default(0).notNull(),
  bestStreak: integer().default(0).notNull(),

  // Timestamps
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  uniqueIndex('UserGameStats_user_game_key').using('btree',
    table.userId.asc().nullsLast(),
    table.gameId.asc().nullsLast()
  ),
  index('UserGameStats_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('UserGameStats_gameId_idx').using('btree', table.gameId.asc().nullsLast()),
  index('UserGameStats_totalScore_idx').using('btree', table.totalScore.desc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'UserGameStats_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.gameId],
    foreignColumns: [game.id],
    name: 'UserGameStats_gameId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

// ==========================================
// PLAYER REPORT - Жалобы на игроков
// ==========================================
export const playerReport = pgTable('PlayerReport', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  // Кто пожаловался
  reporterUserId: integer().notNull(),

  // На кого пожаловались
  reportedUserId: integer().notNull(),

  // Контекст
  sessionId: integer(),
  lobbyId: integer(),

  // Жалоба
  category: playerReportCategory().notNull(),
  description: text(),

  // Модерация
  status: playerReportStatus().default('PENDING').notNull(),
  reviewedByAdminId: integer(),
  reviewedAt: timestamp({ precision: 3, mode: 'date' }),
  resolution: text(),

  // Timestamps
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('PlayerReport_reporterUserId_idx').using('btree', table.reporterUserId.asc().nullsLast()),
  index('PlayerReport_reportedUserId_idx').using('btree', table.reportedUserId.asc().nullsLast()),
  index('PlayerReport_status_idx').using('btree', table.status.asc().nullsLast()),
  index('PlayerReport_sessionId_idx').using('btree', table.sessionId.asc().nullsLast()),
  foreignKey({
    columns: [table.reporterUserId],
    foreignColumns: [user.id],
    name: 'PlayerReport_reporterUserId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.reportedUserId],
    foreignColumns: [user.id],
    name: 'PlayerReport_reportedUserId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.sessionId],
    foreignColumns: [gameSession.id],
    name: 'PlayerReport_sessionId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.lobbyId],
    foreignColumns: [gameLobby.id],
    name: 'PlayerReport_lobbyId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.reviewedByAdminId],
    foreignColumns: [adminUser.id],
    name: 'PlayerReport_reviewedByAdminId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

// ==========================================
// ACHIEVEMENT - Определение достижения
// ==========================================
export const achievement = pgTable('Achievement', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  // Идентификатор
  code: text().notNull(),

  // Информация
  name: text().notNull(),
  description: text(),
  iconUrl: text(),
  // Категория и привязка к игре
  category: achievementCategory().notNull(),
  gameId: integer(), // null = общее достижение

  // Условие получения
  conditionType: achievementConditionType().notNull(),
  condition: jsonb().notNull(), // { metric: 'card_wins', target: 10 }

  // Награда
  rewardPoints: integer().default(0).notNull(),

  // Флаги
  isActive: boolean().default(true).notNull(),

  // Timestamps
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  uniqueIndex('Achievement_code_key').using('btree', table.code.asc().nullsLast()),
  index('Achievement_category_idx').using('btree', table.category.asc().nullsLast()),
  index('Achievement_gameId_idx').using('btree', table.gameId.asc().nullsLast()),
  index('Achievement_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
  foreignKey({
    columns: [table.gameId],
    foreignColumns: [game.id],
    name: 'Achievement_gameId_fkey'
  }).onUpdate('cascade').onDelete('set null'),
]);

// ==========================================
// USER ACHIEVEMENT - Прогресс пользователя по достижениям
// ==========================================
export const userAchievement = pgTable('UserAchievement', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),

  userId: integer().notNull(),
  achievementId: integer().notNull(),

  // Прогресс
  progress: integer().default(0).notNull(), // Текущий прогресс к цели
  isUnlocked: boolean().default(false).notNull(),
  unlockedAt: timestamp({ precision: 3, mode: 'date' }),

  // Timestamps
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  // Один прогресс на пользователя/достижение
  uniqueIndex('UserAchievement_user_achievement_key').using('btree',
    table.userId.asc().nullsLast(),
    table.achievementId.asc().nullsLast()
  ),
  index('UserAchievement_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('UserAchievement_achievementId_idx').using('btree', table.achievementId.asc().nullsLast()),
  index('UserAchievement_isUnlocked_idx').using('btree', table.isUnlocked.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'UserAchievement_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.achievementId],
    foreignColumns: [achievement.id],
    name: 'UserAchievement_achievementId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);
